import { useEffect, useMemo } from 'react';
import { useCalendarStore } from '../../stores/calendarStore';
import { useCalendarSharingStore } from '../../stores/calendarSharingStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { colorForPerson, presentWith } from '../presentActivities';
import type { ShareLevel } from '../sharing';
import { atMinutes, busyIntervals, clampToDay, freeIntervals, mergeIntervals, type Interval } from './availability';
import { blocksFor, workWindowFor } from './workHours';
import { unavailabilityBlocksFor, unavailabilityIdOf } from './unavailability';
import { useCalendarUnavailabilityStore } from '../../stores/calendarUnavailabilityStore';
import { eventOutcomeLabel } from '../outcomeLabel';

/** `block`: pausa ricorrente o indisponibilità della persona, non una task commerciale; `unavailabilityId` se è un'indisponibilità (si apre al clic) */
export interface TeamAppointment extends Interval { id: string; title: string; color: string; busyOnly: boolean; done?: boolean; block?: boolean; unavailabilityId?: string | null; /** etichetta breve dell'esito (task chiuse) */ outcome?: string | null }
export interface TeamColumnData {
  id: string;
  name: string;
  color: string;
  level: ShareLevel;
  appointments: TeamAppointment[];
  busy: Interval[];
  work: Interval | null;
  free: Interval[];
}

/**
 * Le colonne della vista Team per un giorno: persone che vedo (filtrate per reparto e scelta),
 * i loro impegni gia' resi secondo il livello di condivisione, i blocchi ricorrenti e i buchi liberi
 * nell'orario di lavoro di ciascuno (Impostazioni › Calendario › Orari).
 */
export function useTeamColumns(day: Date, departmentId: string, picked: readonly string[] | null) {
  const { people, visible, departments, ctx, prefs } = useCalendarSharingStore();
  const salesActivities = useCalendarStore(s => s.salesActivities);
  const fetchSalesActivities = useCalendarStore(s => s.fetchSalesActivities);
  const orgCal = (useSettingsStore(s => s.settings) as any)?.calendar_settings || {};
  const dayKey = day.toDateString();
  const unavailabilityRows = useCalendarUnavailabilityStore(s => s.rows);
  const fetchUnavailability = useCalendarUnavailabilityStore(s => s.fetchRange);
  const unavailability = useMemo(() => Object.values(unavailabilityRows), [unavailabilityRows]);

  // Gli impegni del giorno (e un giorno di margine) per tutti i calendari che vedo
  useEffect(() => {
    if (!ctx) return;
    void fetchSalesActivities(atMinutes(day, -24 * 60), atMinutes(day, 48 * 60));
    void fetchUnavailability(ctx.organizationId, atMinutes(day, 0), atMinutes(day, 24 * 60));
  }, [dayKey, ctx?.meId, Object.keys(visible).length]); // eslint-disable-line react-hooks/exhaustive-deps

  const seen = useMemo(() => {
    const me = ctx?.meId;
    return people.filter(p => visible[p.id]).sort((a, b) => (a.id === me ? -1 : b.id === me ? 1 : a.name.localeCompare(b.name, 'it')));
  }, [people, visible, ctx?.meId]);

  const inDepartment = useMemo(
    () => (departmentId === 'all' ? seen : seen.filter(p => p.departmentIds.includes(departmentId))),
    [seen, departmentId],
  );
  const chosen = useMemo(
    () => (picked ? inDepartment.filter(p => picked.includes(p.id)) : inDepartment),
    [inDepartment, picked],
  );

  const columns = useMemo<TeamColumnData[]>(() => {
    if (!ctx) return [];
    const ids = chosen.map(p => p.id);
    const presented = presentWith(salesActivities, {
      meId: ctx.meId,
      visible,
      shown: Object.fromEntries(ids.map(id => [id, true as const])),
      colorMode: 'person',
      orderedPeople: people.map(p => p.id),
      personColors: prefs.person_colors,
      typeColors: orgCal.type_colors,
    });
    return chosen.map(p => {
      const work = workWindowFor(day, orgCal.availability, p.id);
      // Pause ricorrenti (Impostazioni › Calendario › Orari) e indisponibilità con date (dal calendario)
      const blocks = [...blocksFor(day, orgCal.availability, p.id), ...unavailabilityBlocksFor(day, unavailability, p.id)];
      const mine = presented.filter(e => (e.metadata as any)?.owner_id === p.id);
      const appointments: TeamAppointment[] = [];
      for (const e of mine) {
        const c = clampToDay(e, day);
        if (!c || e.all_day) continue;
        const busyOnly = !!(e.metadata as any)?.busy_only;
        appointments.push({ ...c, id: e.id, title: e.title, busyOnly, done: !!(e.metadata as any)?.done, outcome: eventOutcomeLabel(e.metadata as any), color: busyOnly ? '#9CA3AF' : e.color || colorForPerson(p.id, ctx.meId, people.map(x => x.id), prefs.person_colors) });
      }
      for (const b of blocks) appointments.push({ ...b, id: `block_${b.id}`, busyOnly: true, block: true, unavailabilityId: unavailabilityIdOf(b.id), color: '#9CA3AF' });
      const busy = mergeIntervals([
        ...busyIntervals(mine.map(e => ({ start_time: e.start_time, end_time: e.end_time, all_day: e.all_day })), day),
        ...blocks,
      ]);
      return {
        id: p.id, name: p.name, level: visible[p.id],
        color: colorForPerson(p.id, ctx.meId, people.map(x => x.id), prefs.person_colors),
        appointments: appointments.sort((a, b) => a.start - b.start), busy, work, free: freeIntervals(busy, work),
      };
    });
  }, [chosen, salesActivities, visible, ctx, people, prefs.person_colors, orgCal.type_colors, orgCal.availability, unavailability, day]);

  return { seen, inDepartment, columns, departments };
}
